import { printHTML, downloadWord } from '../utils/printHelper';
import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Printer, Plus, Trash, ArrowsClockwise, FileDoc } from '@phosphor-icons/react';
import { api } from '../App';
import { DateInput } from '../components/DateInput';
import { toast } from 'sonner';
import ClientAutocomplete from '../components/ClientAutocomplete';

const STORAGE_KEY = 'shared_odluka_data';
const SMJENE = ['I', 'II', 'III', 'SL', '-'];
const DANI_LABELS = ['P', 'U', 'S', 'Č', 'P', 'S', 'N'];

function loadShared() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; }
}
function saveShared(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function OdlukaSmjenePage() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    naziv_drustva: '',
    grad: 'Ulcinj',
    objekat_naziv: '',
    objekat_adresa: 'Donji Stoj bb',
    datum: new Date().toISOString().split('T')[0],
    period_od: '',
    period_do: '',
    direktor: '',
    smjena1_od: '',
    smjena1_do: '',
    smjena2_od: '',
    smjena2_do: '',
    smjena3_od: '',
    smjena3_do: '',
  });
  const [radnici, setRadnici] = useState([
    { ime_prezime: '', radno_mjesto: '', dani: ['', '', '', '', '', '', ''] }
  ]);
  const [loadingRadnici, setLoadingRadnici] = useState(false);

  useEffect(() => {
    api.get('/clients')
      .then(r => {
        const sorted = r.data.sort((a, b) => (a.skraceni_naziv || '').localeCompare(b.skraceni_naziv || ''));
        setClients(sorted);

        const shared = loadShared();
        if (shared) {
          if (shared.client_id) {
            const c = sorted.find(cl => cl.id === (shared.form?.client_id || shared.client_id));
            setForm(prev => ({
              ...prev,
              client_id: shared.form?.client_id || '',
              naziv_drustva: c ? (c.skraceni_naziv || c.naziv_firme || '') : (shared.form?.naziv_drustva || ''),
              grad: shared.form?.grad || 'Ulcinj',
              objekat_naziv: shared.form?.objekat_naziv || '',
              period_od: shared.form?.period_od || '',
              period_do: shared.form?.period_do || '',
              direktor: shared.form?.direktor || '',
            }));
          }
          if (shared.radnici?.length) {
            setRadnici(shared.radnici.map(r => ({
              ime_prezime: r.ime_prezime || '',
              radno_mjesto: r.radno_mjesto || '',
              dani: r.dani || ['', '', '', '', '', '', ''],
            })));
          }
        }
      });
  }, []);

  useEffect(() => {
    if (form.client_id) {
      const shared = loadShared() || {};
      saveShared({ ...shared, form, radnici, client_id: form.client_id });
    }
  }, [form, radnici]);

  const fetchAktivniRadnici = async (clientId) => {
    setLoadingRadnici(true);
    try {
      const r = await api.get(`/prijava-radnika?client_id=${clientId}&status=aktivan`);
      const workers = r.data;
      if (workers.length > 0) {
        setRadnici(workers.map(w => ({
          ime_prezime: `${(w.prezime || '').toUpperCase()} ${(w.ime || '').toUpperCase()}`.trim(),
          radno_mjesto: w.naziv_radnog_mjesta || '',
          dani: ['', '', '', '', '', '', ''],
        })));
        toast.success(`Učitano ${workers.length} aktivnih radnika`);
      } else {
        toast.info('Nema aktivnih radnika za ovog klijenta');
      }
    } catch {
      toast.error('Greška pri učitavanju radnika');
    } finally {
      setLoadingRadnici(false);
    }
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    if (c) {
      setForm(prev => ({
        ...prev,
        client_id: clientId,
        naziv_drustva: c.skraceni_naziv || c.naziv_firme || '',
        grad: c.grad || 'Ulcinj',
        objekat_naziv: c.skraceni_naziv || c.naziv_firme || '',
        objekat_adresa: c.adresa || 'Donji Stoj bb',
        direktor: c.direktor || '',
      }));
      fetchAktivniRadnici(clientId);
    }
  };

  const addRadnik = () => setRadnici([...radnici, { ime_prezime: '', radno_mjesto: '', dani: ['', '', '', '', '', '', ''] }]);
  const removeRadnik = (i) => setRadnici(radnici.filter((_, idx) => idx !== i));
  const updateRadnik = (i, field, value) => {
    const updated = [...radnici];
    updated[i][field] = value;
    setRadnici(updated);
  };
  const updateRadnikDan = (i, dayIdx, value) => {
    const updated = [...radnici];
    updated[i].dani[dayIdx] = value;
    setRadnici(updated);
  };

  const formatDatePrint = (d) => {
    if (!d) return '___________';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return d;
  };

  const handlePrint = () => {
    const filledRadnici = radnici.filter(r => r.ime_prezime.trim());
    const tableRows = filledRadnici.map((r, i) => {
      const daniCells = (r.dani || []).map(d => `<td style="text-align:center;padding:2px 1px;border:1px solid #000;font-size:8pt">${d || ''}</td>`).join('');
      return `<tr>
        <td style="text-align:center;padding:2px 3px;border:1px solid #000;font-size:8pt">${i + 1}.</td>
        <td style="padding:2px 4px;border:1px solid #000;font-size:8pt;white-space:nowrap">${r.ime_prezime || ''}</td>
        <td style="padding:2px 4px;border:1px solid #000;font-size:8pt;white-space:nowrap">${r.radno_mjesto || ''}</td>
        ${daniCells}
      </tr>`;
    }).join('');

    const periodText = form.period_od && form.period_do
      ? `${formatDatePrint(form.period_od)} - ${formatDatePrint(form.period_do)}`
      : '____________________________';

    // Build shift lines - only show shifts that have data
    let smjeneHtml = '';
    if (form.smjena1_od || form.smjena1_do) {
      smjeneHtml += `<div class="smjene-row">- I smjena od <strong>${form.smjena1_od || '________'}</strong> do <strong>${form.smjena1_do || '________'}</strong> h.</div>`;
    }
    if (form.smjena2_od || form.smjena2_do) {
      smjeneHtml += `<div class="smjene-row">- II smjena od <strong>${form.smjena2_od || '________'}</strong> do <strong>${form.smjena2_do || '________'}</strong> h.</div>`;
    }
    if (form.smjena3_od || form.smjena3_do) {
      smjeneHtml += `<div class="smjene-row">- III smjena od <strong>${form.smjena3_od || '________'}</strong> do <strong>${form.smjena3_do || '________'}</strong> h.</div>`;
    }

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Odluka o rasporedu po smjenama</title>
<style>
@page{size:A4 landscape;margin:15mm 20mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:12pt;color:#000;line-height:1.5;background:#fff}
.doc{margin:0 auto}
.header{font-size:11pt;margin-bottom:5px}
.header-firm{font-weight:bold;font-size:12pt}
.header-row{font-size:11pt;margin:3px 0}
.header-line{border-bottom:1px solid #000;min-width:150px;display:inline-block;padding:0 4px}
.title{text-align:center;font-weight:bold;font-size:14pt;margin:15px 0 5px}
.subtitle{text-align:center;font-size:11pt;margin-bottom:12px}
p{font-size:11pt;line-height:1.45;margin:5px 0;text-align:justify}
.smjene{margin:8px 0;font-size:11pt}
.smjene-row{margin:2px 0 2px 15px}
table.workers{width:100%;border-collapse:collapse;margin:8px 0}
table.workers th{background:#f0f0f0;font-weight:bold;padding:3px 2px;border:1px solid #000;font-size:8pt;text-align:center}
table.workers td{vertical-align:middle}
.period{margin:12px 0;font-size:11pt}
.obrazlozenje{margin-top:12px}
.obrazlozenje-title{font-weight:bold;font-size:11pt;text-decoration:underline;margin-bottom:4px}
.dostavljeno{margin-top:12px;font-size:10.5pt;line-height:1.5}
.sign-area{display:flex;justify-content:flex-end;margin-top:20px;font-size:11pt}
.sign-right{text-align:center}
.sign-line{border-top:1px solid #000;min-width:180px;padding-top:3px;margin-top:25px}
.sign-sub{font-size:9.5pt;margin-top:2px}
@media print{body{margin:0}table.workers th{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="doc">
<div class="header">
  <div class="header-firm">${form.naziv_drustva}</div>
  <div class="header-row">Broj: <span class="header-line">&nbsp;</span></div>
  <div class="header-row">${form.grad.toUpperCase()}: <span class="header-line">${formatDatePrint(form.datum)}</span> god.</div>
</div>

<p style="margin-top:8px;font-size:10.5pt">Na osnovu člana 67 stav 3 ZAKON O RADU (&bdquo;Sl. list CG&ldquo;, br.74/2019, 8/2021, 59/2021, 68/2021, 145/2021, 77/2024, 84/2024 - drugi zakon, 86/2024, 122/2025 i 165/2025), izvršni direktor/preduzetnik u ${form.naziv_drustva} donosi</p>

<div class="title">ODLUKU</div>
<div class="subtitle">- o rasporedu radnog vremena i raspored zaposlenih po smjenama -</div>

<p>Zaposlenima u <strong>${form.objekat_naziv}</strong> sa sjedištem u ${form.grad}, ${form.objekat_adresa} utvrđuje se rad po smjenama, s tim što je:</p>

<div class="smjene">
  ${smjeneHtml}
</div>

<p>Obzirom da priroda posla i organizacija rada to zahtijevaju kod je organizovan raspored radnog vremena po smjenama, pa se zaposlenima utvrđuje raspored radnog vremena/smjena kako slijedi:</p>

<table class="workers">
  <thead>
    <tr>
      <th style="width:25px">R.<br>br.</th>
      <th>Ime i prezime zaposlenog</th>
      <th>Radno mjesto</th>
      <th style="width:28px">P</th>
      <th style="width:28px">U</th>
      <th style="width:28px">S</th>
      <th style="width:28px">Č</th>
      <th style="width:28px">P</th>
      <th style="width:28px">S</th>
      <th style="width:28px">N</th>
    </tr>
  </thead>
  <tbody>
    ${tableRows}
  </tbody>
</table>

<div class="period">Odluka se donosi na period od <strong>${periodText}</strong> .godine.</div>

<div class="obrazlozenje">
  <div class="obrazlozenje-title">Obrazloženje</div>
  <p style="font-size:10.5pt">Odredbama člana 67 Zakona o radu ZAKON O RADU (&bdquo;Sl. list CG&ldquo;, br.74/2019, 8/2021, 59/2021, 68/2021, 145/2021, 77/2024, 84/2024 - drugi zakon, 86/2024, 122/2025 i 165/2025) propisano je da je poslodavac dužan da donese pisanu odluku o rasporedu radnog vremena zaposlenih i njihov raspored po smjenama, ako je kod tog poslodavca rad organizovan po smjenama. Na osnovu iznijetog odlučeno je kao u dispozitivu.</p>
</div>

<div class="dostavljeno">
  Dostavljeno:<br>
  - zaposlenima,<br>
  - na oglasnoj tabli poslodavca,<br>
  - a/a.
</div>

<div class="sign-area">
  <div class="sign-right">
    POSLODAVAC
    <div class="sign-line">(izvršni direktor/preduzetnik)</div>
  </div>
</div>
</div></body></html>`;
    printHTML(html);
  };

  const handleWord = () => {
    const filledRadnici = radnici.filter(r => r.ime_prezime.trim());
    if (filledRadnici.length === 0) return;
    // Re-build the same HTML
    const tableRows = filledRadnici.map((r, i) => {
      const daniCells = (r.dani || []).map(d => `<td style="text-align:center;padding:2px 1px;border:1px solid #000;font-size:8pt">${d || ''}</td>`).join('');
      return `<tr><td style="text-align:center;padding:2px 3px;border:1px solid #000;font-size:8pt">${i+1}.</td><td style="padding:2px 4px;border:1px solid #000;font-size:8pt">${r.ime_prezime||''}</td><td style="padding:2px 4px;border:1px solid #000;font-size:8pt">${r.radno_mjesto||''}</td>${daniCells}</tr>`;
    }).join('');
    const periodText = form.period_od && form.period_do ? `${formatDatePrint(form.period_od)} - ${formatDatePrint(form.period_do)}` : '____';
    let smjeneHtml = '';
    if (form.smjena1_od||form.smjena1_do) smjeneHtml += `<div>- I smjena od <strong>${form.smjena1_od||'__'}</strong> do <strong>${form.smjena1_do||'__'}</strong> h.</div>`;
    if (form.smjena2_od||form.smjena2_do) smjeneHtml += `<div>- II smjena od <strong>${form.smjena2_od||'__'}</strong> do <strong>${form.smjena2_do||'__'}</strong> h.</div>`;
    if (form.smjena3_od||form.smjena3_do) smjeneHtml += `<div>- III smjena od <strong>${form.smjena3_od||'__'}</strong> do <strong>${form.smjena3_do||'__'}</strong> h.</div>`;
    const html2 = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:Arial;font-size:12pt}table{width:100%;border-collapse:collapse}th,td{border:1px solid #000;padding:4px}</style></head><body><h2 style="text-align:center">ODLUKA - o rasporedu radnog vremena i raspored zaposlenih po smjenama</h2><p>${form.naziv_drustva}, ${form.grad}</p><div>${smjeneHtml}</div><table><thead><tr><th>R.br.</th><th>Ime i prezime</th><th>Radno mjesto</th><th>P</th><th>U</th><th>S</th><th>Č</th><th>P</th><th>S</th><th>N</th></tr></thead><tbody>${tableRows}</tbody></table><p>Period: ${periodText}</p></body></html>`;
    downloadWord(html2, `Raspored_smjena_${(form.naziv_drustva||'').replace(/\s+/g,'_')}.docx`);
  };

  return (
    <div className="space-y-4" data-testid="odluka-smjene-page">
      <Card className="p-4 bg-white border-slate-200">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Odluka o rasporedu po smjenama</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Klijent</Label>
            <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => (handleClientSelect)(id || 'none')} testid="smjene-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Naziv objekta</Label>
            <Input value={form.objekat_naziv} onChange={e => setForm({...form, objekat_naziv: e.target.value})} className="bg-white border-slate-300 text-sm" data-testid="smjene-objekat-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Adresa objekta</Label>
            <Input value={form.objekat_adresa} onChange={e => setForm({...form, objekat_adresa: e.target.value})} className="bg-white border-slate-300 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Grad</Label>
            <Input value={form.grad} onChange={e => setForm({...form, grad: e.target.value})} className="bg-white border-slate-300 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Datum odluke</Label>
            <DateInput value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} className="border-slate-300 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Direktor</Label>
            <Input value={form.direktor} readOnly className="bg-slate-50 border-slate-300 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Period od</Label>
            <DateInput value={form.period_od} onChange={e => setForm({...form, period_od: e.target.value})} className="border-slate-300 text-sm" data-testid="smjene-period-od" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Period do</Label>
            <DateInput value={form.period_do} onChange={e => setForm({...form, period_do: e.target.value})} className="border-slate-300 text-sm" data-testid="smjene-period-do" />
          </div>
        </div>

        <div className="mt-3 p-3 bg-slate-50 rounded border border-slate-200">
          <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Smjene</h4>
          <div className="space-y-2">
            <div className="grid grid-cols-[auto_1fr_auto_1fr] gap-2 items-center">
              <span className="text-xs text-slate-600 font-medium">I smjena od</span>
              <Input value={form.smjena1_od} onChange={e => setForm({...form, smjena1_od: e.target.value})} placeholder="08:00" className="bg-white border-slate-300 text-sm" data-testid="smjena1-od" />
              <span className="text-xs text-slate-600 font-medium">do</span>
              <Input value={form.smjena1_do} onChange={e => setForm({...form, smjena1_do: e.target.value})} placeholder="16:00" className="bg-white border-slate-300 text-sm" data-testid="smjena1-do" />
            </div>
            <div className="grid grid-cols-[auto_1fr_auto_1fr] gap-2 items-center">
              <span className="text-xs text-slate-600 font-medium">II smjena od</span>
              <Input value={form.smjena2_od} onChange={e => setForm({...form, smjena2_od: e.target.value})} placeholder="16:00" className="bg-white border-slate-300 text-sm" data-testid="smjena2-od" />
              <span className="text-xs text-slate-600 font-medium">do</span>
              <Input value={form.smjena2_do} onChange={e => setForm({...form, smjena2_do: e.target.value})} placeholder="00:00" className="bg-white border-slate-300 text-sm" data-testid="smjena2-do" />
            </div>
            <div className="grid grid-cols-[auto_1fr_auto_1fr] gap-2 items-center">
              <span className="text-xs text-slate-600 font-medium">III smjena od</span>
              <Input value={form.smjena3_od} onChange={e => setForm({...form, smjena3_od: e.target.value})} placeholder="00:00" className="bg-white border-slate-300 text-sm" data-testid="smjena3-od" />
              <span className="text-xs text-slate-600 font-medium">do</span>
              <Input value={form.smjena3_do} onChange={e => setForm({...form, smjena3_do: e.target.value})} placeholder="08:00" className="bg-white border-slate-300 text-sm" data-testid="smjena3-do" />
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-700">Spisak zaposlenih i raspored smjena</h3>
          <div className="flex gap-2">
            {form.client_id && (
              <Button onClick={() => fetchAktivniRadnici(form.client_id)} variant="outline" size="sm" className="text-xs border-blue-300 text-blue-600 hover:bg-blue-50" disabled={loadingRadnici}>
                <ArrowsClockwise size={14} className={`mr-1 ${loadingRadnici ? 'animate-spin' : ''}`} /> {loadingRadnici ? 'Učitavam...' : 'Osvježi iz baze'}
              </Button>
            )}
            <Button onClick={addRadnik} variant="outline" size="sm" className="text-xs" data-testid="smjene-add-radnik">
              <Plus size={14} className="mr-1" /> Dodaj radnika
            </Button>
          </div>
        </div>
        <div className="text-xs text-slate-400 mb-2">Smjene: I = prva, II = druga, III = treća, SL = slobodan, - = ne radi</div>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_1fr_repeat(7,48px)_36px] gap-1 items-center">
            <span className="text-xs font-bold text-slate-500">Ime i prezime</span>
            <span className="text-xs font-bold text-slate-500">Radno mjesto</span>
            {DANI_LABELS.map((d, idx) => (
              <span key={idx} className="text-xs font-bold text-slate-500 text-center">{d}</span>
            ))}
            <span></span>
          </div>
          {radnici.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_repeat(7,48px)_36px] gap-1 items-center">
              <Input
                value={r.ime_prezime}
                onChange={e => updateRadnik(i, 'ime_prezime', e.target.value)}
                placeholder="Ime i prezime"
                className="bg-white border-slate-300 text-xs h-8"
                data-testid={`smjene-radnik-ime-${i}`}
              />
              <Input
                value={r.radno_mjesto}
                onChange={e => updateRadnik(i, 'radno_mjesto', e.target.value)}
                placeholder="Radno mjesto"
                className="bg-white border-slate-300 text-xs h-8"
              />
              {(r.dani || ['','','','','','','']).map((d, dayIdx) => (
                <Select key={dayIdx} value={d} onValueChange={v => updateRadnikDan(i, dayIdx, v)}>
                  <SelectTrigger className="bg-white border-slate-300 text-xs h-8 px-1">
                    <SelectValue placeholder="-" />
                  </SelectTrigger>
                  <SelectContent>
                    {SMJENE.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ))}
              <button onClick={() => removeRadnik(i)} className="p-1 hover:bg-red-100 rounded" data-testid={`smjene-remove-radnik-${i}`}>
                <Trash size={14} className="text-red-500" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex gap-2">
        <Button onClick={handlePrint} className="bg-slate-800 hover:bg-slate-700 text-sm" disabled={!form.client_id} data-testid="print-smjene-btn">
          <Printer size={16} className="mr-2" /> Štampaj
        </Button>
        <Button onClick={handleWord} className="bg-indigo-600 hover:bg-indigo-700 text-sm text-white" disabled={!form.client_id}>
          <FileDoc size={16} className="mr-2" /> Word
        </Button>
      </div>
    </div>
  );
}
